import React, { useCallback, useMemo, memo } from 'react';
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { BaseQuestion } from '../../../features/variety-planner/types';
import AppTooltip from '../AppTooltip';
import AppRadio from '../AppRadio';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { CropConnectLanguages } from '../../../../services/cropConnectApiClient';

const makeStyles = () => ({
  questionContainer: {
    marginBottom: 1,
  },
  answerOption: {
    marginLeft: 0,
    marginRight: 1,
  },
});

interface YesNoQuestionProps {
  question: BaseQuestion;
  value: string | string[];
  onChange: (value: string) => void;
}

const YesNoQuestion: React.FC<Readonly<YesNoQuestionProps>> = ({
  question,
  value,
  onChange,
}) => {
  const styles = makeStyles();
  //Forced to DE lng, not edtiable in translation modal as per business requirement on this component

  const { t } = useTranslation();

  const stringValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : '';
    }
    return value || '';
  }, [value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );
  const questionKey =
    `varietyPlanner_${question.tag}` as keyof typeof TranslationKeys;
  const tooltipKey =
    `varietyPlanner_${question.tag}_Info` as keyof typeof TranslationKeys;

  return (
    <Box>
      <Box sx={styles.questionContainer}>
        <Typography variant="appH5" color="questionsForm.formLabel">
          {t(questionKey as TranslationKeys, {
            lng: CropConnectLanguages.DeDE,
          })}
        </Typography>
        {question.hasInfo && (
          <AppTooltip
            title={t(tooltipKey as TranslationKeys, {
              lng: CropConnectLanguages.DeDE,
            })}
            tooltipContentSx={{ marginLeft: 0.75 }}
          />
        )}
      </Box>

      <FormControl component="fieldset">
        <RadioGroup row value={stringValue} name={question.id}>
          {question.options &&
            question.options
              .sort(
                (firstOption, secondOption) =>
                  (firstOption.order as number) - (secondOption.order as number)
              )
              .map((option) => {
                const optionKey =
                  `varietyPlanner_${question.tag}_${option.type?.toUpperCase()}_TAG` as keyof typeof TranslationKeys;

                return (
                  <FormControlLabel
                    value={option.id}
                    key={option.id}
                    labelPlacement="start"
                    control={
                      <AppRadio
                        checked={stringValue === option.id}
                        onChange={handleChange}
                      />
                    }
                    sx={styles.answerOption}
                    label={
                      <Typography
                        variant="appBodyMedium"
                        color="questionsForm.formGrey"
                      >
                        {t(optionKey as TranslationKeys, {
                          lng: CropConnectLanguages.DeDE,
                        })}
                      </Typography>
                    }
                  />
                );
              })}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default memo(YesNoQuestion);
